import { assetUrl } from '../../ui/assetUrl'
import { BOX_PADDING, boxElements, officeRank, type OfficeGroups } from './officeBoxes'
import { MAX_ZOOM, MIN_ZOOM } from './zoom'

/*
 * Desenul hartii de inventar - perechea lui `public/js/inventoree/inventory_map.js`
 * din portalul vechi. Serverul (`InventoryMapGraph`) hotaraste CE se vede si de
 * unde stim fiecare lucru; fisierul asta doar deseneaza, cu aceleasi reguli:
 * ce e MASURAT se deseneaza plin, ce e PRESUPUS - intrerupt, ce a fost VAZUT dar
 * nu e in inventar - punctat.
 *
 * Biblioteca e cytoscape, aceeasi cu harta ADM din Softsee - se incarca dintr-un
 * singur `<script>` al portalului (`loadCytoscape`).
 */

export const COLORS: Record<string, string> = {
  internet: '#6b7480',
  egress: '#7d5ba6',
  network: '#3c8dbc',
  gateway: '#B16131',
  switch: '#3c8dbc',
  ap: '#B16131',
  server: '#1FA67A',
  workstation: '#37addb',
  laptop: '#37addb',
  mobile: '#9b59b6',
  printer: '#6b7480',
  discovered: '#9aa4b1',
}

/*
 * Iconitele obisnuite din schemele de retea, desenate ca SVG - la fel ca in
 * `inventory_map.js`. Nu se aduc dintr-o fonta: cytoscape deseneaza pe panza,
 * iar acolo un caracter de fonta nu e o imagine. SVG scris in cod ramane clar la
 * orice marime si nu adauga niciun fisier de incarcat.
 */
const GLYPHS: Record<string, string> = {
  internet: '<path d="M7.5 19h9.8a3.7 3.7 0 0 0 .5-7.4A5.2 5.2 0 0 0 8 9.6 4.2 4.2 0 0 0 7.5 19z"/>',
  egress:
    '<circle cx="12" cy="12" r="9.2"/>' +
    '<path fill="#fff" d="M12 3.4c-1.9 0-3.6 3.9-3.6 8.6s1.7 8.6 3.6 8.6 3.6-3.9 3.6-8.6S13.9 3.4 12 3.4zm0 1.8c.8 0 2 2.8 2 6.8s-1.2 6.8-2 6.8-2-2.8-2-6.8 1.2-6.8 2-6.8z"/>' +
    '<path fill="#fff" d="M3.3 9.4h17.4V11H3.3zM3.3 13h17.4v1.6H3.3z"/>',
  network:
    '<path d="M3 13h18v1.6H3z"/><path d="M11.2 5h1.6v8h-1.6z"/>' +
    '<rect x="8" y="2.5" width="8" height="3.5" rx="1"/>' +
    '<rect x="3" y="16" width="5" height="3.5" rx="1"/>' +
    '<rect x="9.5" y="16" width="5" height="3.5" rx="1"/>' +
    '<rect x="16" y="16" width="5" height="3.5" rx="1"/>' +
    '<path d="M5.2 14h1v2.4h-1zM11.5 14h1v2.4h-1zM18 14h1v2.4h-1z"/>',
  gateway:
    '<circle cx="12" cy="12" r="9.2"/>' +
    '<path fill="#fff" d="M7 9.4h6.4V7.2L17.4 10l-4 2.8v-2.2H7z"/>' +
    '<path fill="#fff" d="M17 14.6h-6.4v2.2L6.6 14l4-2.8v2.2H17z"/>',
  switch:
    '<rect x="2.2" y="6.5" width="19.6" height="11" rx="1.6"/>' +
    '<path fill="#fff" d="M6 10h7.2V8.3l3.2 2.2-3.2 2.2V11H6z"/>' +
    '<path fill="#fff" d="M18 14h-7.2v1.7l-3.2-2.2 3.2-2.2V13H18z"/>',
  ap:
    '<rect x="5" y="14" width="14" height="6" rx="1.6"/>' +
    '<circle cx="12" cy="17" r="1.2" fill="#fff"/>' +
    '<path d="M8.6 10.4a4.8 4.8 0 0 1 6.8 0l-1.2 1.2a3.1 3.1 0 0 0-4.4 0z"/>' +
    '<path d="M6.2 8a8.2 8.2 0 0 1 11.6 0l-1.2 1.2a6.5 6.5 0 0 0-9.2 0z"/>',
  server:
    '<rect x="5" y="2.5" width="14" height="19" rx="1.6"/>' +
    '<path fill="#fff" d="M7.4 5.4h9.2v2.2H7.4zM7.4 9.4h9.2v2.2H7.4zM7.4 13.4h9.2v2.2H7.4z"/>' +
    '<circle cx="15.4" cy="18.4" r="1" fill="#fff"/>',
  workstation:
    '<rect x="2.5" y="4" width="19" height="12.4" rx="1.6"/>' +
    '<rect x="4.6" y="6" width="14.8" height="8.4" fill="#fff"/>' +
    '<path d="M9 17.6h6l1 3.2H8z"/>',
  laptop:
    '<rect x="4" y="4.6" width="16" height="10.4" rx="1.4"/>' +
    '<rect x="5.8" y="6.4" width="12.4" height="6.8" fill="#fff"/>' +
    '<path d="M2 16.6h20l-1.4 2.8H3.4z"/>',
  mobile:
    '<rect x="7" y="2.4" width="10" height="19.2" rx="2"/>' +
    '<rect x="8.6" y="5" width="6.8" height="12" fill="#fff"/>' +
    '<circle cx="12" cy="19" r="1" fill="#fff"/>',
  printer:
    '<path d="M7 3h10v4H7z"/>' +
    '<rect x="3" y="7.6" width="18" height="8.4" rx="1.6"/>' +
    '<circle cx="17.6" cy="10.4" r="1" fill="#fff"/>' +
    '<path d="M7 14h10v7H7z"/><path fill="#fff" d="M8.6 15.6h6.8v1.4H8.6zM8.6 18h6.8v1.4H8.6z"/>',
  discovered:
    '<circle cx="12" cy="12" r="9.2"/>' +
    '<path fill="#fff" d="M11 17h2v2h-2zM12 5.4c2.2 0 3.9 1.5 3.9 3.5 0 1.7-1 2.4-1.9 3-.7.5-1 .8-1 1.6v.6h-2v-.9c0-1.5.8-2.2 1.7-2.8.7-.5 1.2-.8 1.2-1.5 0-.9-.8-1.5-1.9-1.5-1.2 0-2 .7-2 1.8H9c0-2.1 1.6-3.8 3-3.8z"/>',
}

const iconCache: Record<string, string> = {}

/** Iconita unui fel de nod, ca data-URI, compusa o singura data si tinuta minte. */
export function icon(kind: string): string {
  let key = kind
  if (!GLYPHS[key]) {
    key = 'discovered'
  }
  if (!iconCache[key]) {
    iconCache[key] =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="' + (COLORS[key] || '#9aa4b1') + '">' + GLYPHS[key] + '</svg>',
      )
  }
  return iconCache[key]
}

/** Ce ajunge la handler: `ev.target.data()` - datele nodului atins. */
type CyTapEvent = { target: { data: () => Record<string, unknown> } }

/** Elementul primit de o functie de stil: `el.data(cheie)`. */
type StyleEl = { data: (k: string) => unknown }

/** Un nod sau o muchie, cat il foloseste harta: date, vecini, clase. */
type CyEl = {
  length: number
  id: () => string
  position: ((pos: { x: number; y: number }) => CyEl) & (() => { x: number; y: number })
  data: ((k?: string) => unknown) & (() => Record<string, unknown>)
  successors: () => CyColl
  predecessors: () => CyColl
  removeClass: (c: string) => CyColl
  addClass: (c: string) => CyColl
}

/** O multime de elemente. */
type CyColl = {
  length: number
  forEach: (fn: (el: CyEl) => void) => void
  nodes: () => CyColl
  predecessors: () => CyColl
  filter: (fn: (el: CyEl) => boolean) => CyColl
  removeClass: (c: string) => CyColl
  addClass: (c: string) => CyColl
}

export type Cy = {
  destroy: () => void
  ready: (fn: () => void) => void
  resize: () => void
  fit: (eles?: unknown, padding?: number) => void
  zoom: ((level: number | { level: number; renderedPosition: { x: number; y: number } }) => void) & (() => number)
  width: () => number
  height: () => number
  removeListener: (events: string, handler?: (...args: never[]) => void) => void
  pan: ((pos: { x: number; y: number }) => void) & (() => { x: number; y: number })
  center: (eles?: unknown) => void
  elements: (selector?: string) => CyColl
  nodes: (selector?: string) => CyColl
  $id: (id: string) => CyEl
  layout: (o: object) => { run: () => void }
  on: ((events: string, selector: string, handler: (ev: CyTapEvent) => void) => void) &
    ((events: string, handler: (ev: { target: unknown }) => void) => void)
}

declare global {
  interface Window {
    cytoscape?: (opts: Record<string, unknown>) => Cy
  }
}

export function loadCytoscape(): Promise<(opts: Record<string, unknown>) => Cy> {
  if (window.cytoscape) return Promise.resolve(window.cytoscape)
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-cy="imap"]')
    if (existing) {
      existing.addEventListener('load', () => resolve(window.cytoscape!))
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = assetUrl('plugins/cytoscape/cytoscape.min.js')
    s.dataset.cy = 'imap'
    s.onload = () => resolve(window.cytoscape!)
    s.onerror = reject
    document.head.appendChild(s)
  })
}

/** Semnul de dupa numele unui nod: M daca l-a confirmat un om, avertisment daca e ghicit. */
function mark(el: StyleEl): string {
  if (el.data('confirmed')) {
    return '  M'
  }
  return el.data('evidence') === 'assumed' && el.data('kind') !== 'network' ? '  \u26A0' : ''
}

/*
 * Marimea nodului pe panza. 30px arata ca un chenar gol odata ce harta e
 * micsorata ca sa incapa tot - iconita SVG intra in cutie, dar la zoom mic
 * dispare din ochi. 48px se citeste si pe o retea cu zeci de cutii.
 */
export const NODE_SIZE = 48
export const NODE_SIZE_CONFIRMED = 58
export const NODE_SIZE_PUBLIC = 52
export const NODE_SIZE_INTERNET = 104

/** Marimea numelui de sediu de pe cutie. */
const BOX_FONT = 14

let measure: CanvasRenderingContext2D | null | undefined

/**
 * Cat de lat iese un text ingrosat pe panza, in px. Cu un canvas, masurat cu
 * fonta lui cytoscape; fara (jsdom), o aproximare dupa numarul de litere.
 */
export function labelWidth(text: string, px: number): number {
  if (measure === undefined) {
    try {
      measure = document.createElement('canvas').getContext('2d')
    } catch {
      measure = null
    }
  }
  if (measure) {
    measure.font = 'bold ' + px + 'px Helvetica Neue, Helvetica, sans-serif'
    const w = measure.measureText(text).width
    if (w > 0) return Math.ceil(w)
  }
  return Math.ceil(text.length * px * 0.62)
}

/** Stilurile din inventory_map.js: plin / intrerupt / punctat, cu iconita fiecarui fel. */
export function mapStyle(): object[] {
  return [
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'font-size': 11,
        color: '#33414e',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 6,
        'text-wrap': 'wrap',
        'text-max-width': 150,
        'min-zoomed-font-size': 7,
        width: NODE_SIZE,
        height: NODE_SIZE,
        'background-color': '#fff',
        'background-opacity': 0,
        /* Poarta vazuta din afara pastreaza globul: e tot o poarta, dar cealalta
           fata a ei - cea cu care ne vede lumea. */
        'background-image': (el: StyleEl) => icon(el.data('public') ? 'egress' : String(el.data('kind') ?? '')),
        'background-fit': 'contain',
        /* Umple aproape toata cutia: `contain` singur lasa aer pe margini si, pe
           nodurile mici, iconita parea disparuta. */
        'background-width': '90%',
        'background-height': '90%',
        'background-clip': 'none',
        shape: 'round-rectangle',
        'border-width': 0,
      },
    },
    {
      /* Nodurile presupuse: conturate intrerupt; iconita ramane citibila, nu o umbra. */
      selector: 'node[evidence = "assumed"]',
      style: {
        'background-image-opacity': 0.85,
        'border-width': 2,
        'border-style': 'dashed',
        'border-color': (el: StyleEl) => COLORS[String(el.data('kind') ?? '')] || '#9aa4b1',
      },
    },
    {
      /* Semnul de intrebare pe cutiile de retea presupuse. */
      selector: 'node[evidence = "assumed"][kind != "network"]',
      style: {
        label: (el: StyleEl) => String(el.data('label') ?? '') + '  \u26A0',
        color: '#B16131',
      },
    },
    {
      /* Vazut in retea, dar nu in inventar: contur PUNCTAT. */
      selector: 'node[evidence = "unregistered"]',
      style: {
        'background-image-opacity': 0.9,
        'border-width': 2,
        'border-style': 'dotted',
        'border-color': '#7d5ba6',
        color: '#7d5ba6',
      },
    },
    {
      /* Confirmat de un om: mai mare, plin, si cu un M langa. */
      selector: 'node[?confirmed]',
      style: {
        width: NODE_SIZE_CONFIRMED,
        height: NODE_SIZE_CONFIRMED,
        'background-image-opacity': 1,
        'border-width': 0,
        color: '#33414e',
        label: (el: StyleEl) => String(el.data('label') ?? '') + '  M',
      },
    },
    {
      /* Internetul sta in mijloc si e mai mare: e punctul de la care se citeste harta. */
      selector: 'node[kind = "internet"]',
      style: { width: NODE_SIZE_INTERNET, height: NODE_SIZE_INTERNET, 'font-size': 13, 'font-weight': 'bold' },
    },
    {
      /* Comutatorul poarta acum reteaua: numele lui, VLAN-ul si adresele, pe trei randuri. */
      selector: 'node[?vlan]',
      style: {
        label: (el: StyleEl) => {
          const lines = [String(el.data('label') ?? '') + mark(el)]
          lines.push(String(el.data('vlan') ?? '') + ' · ' + String(el.data('cidr') ?? ''))
          const netName = el.data('netName')
          if (netName && netName !== el.data('cidr')) {
            lines.push(String(netName))
          }
          return lines.join('\n')
        },
        'font-size': 9,
        'text-max-width': 160,
      },
    },
    {
      /* Masinile isi scriu adresa sub nume. */
      selector: 'node[deviceId][sublabel]',
      style: {
        label: (el: StyleEl) => String(el.data('label') ?? '') + mark(el) + '\n' + String(el.data('sublabel') ?? ''),
        'font-size': 9,
      },
    },
    {
      /* Poarta vazuta din afara: putin mai mare, fiindca de ea atarna tot ce iese prin acea adresa. */
      selector: 'node[?public]',
      style: { width: NODE_SIZE_PUBLIC, height: NODE_SIZE_PUBLIC, 'font-weight': 'bold' },
    },
    {
      selector: 'edge',
      style: {
        width: 1.5,
        'line-color': '#c7ced6',
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
        label: 'data(label)',
        color: '#5a6570',
        'font-size': 9,
        'text-background-color': '#ffffff',
        'text-background-opacity': 0.85,
        'text-background-padding': 2,
        'text-rotation': 'autorotate',
        'text-margin-y': -6,
      },
    },
    {
      selector: 'edge[evidence = "assumed"]',
      style: { 'line-style': 'dashed', 'line-color': '#d9b38c' },
    },
    {
      /* Wireless se vede si din linie, nu doar din unde e agatata. */
      selector: 'edge[link = "wireless"]',
      style: { 'line-color': '#9b59b6', 'line-style': 'dashed' },
    },
    {
      /* Gazduirea nu e un fir - se deseneaza punctat, ca sa nu fie citita drept cablu. */
      selector: 'edge[link = "virtual"]',
      style: { 'line-color': '#1FA67A', 'line-style': 'dotted', width: 2 },
    },
    {
      /* Ce se stinge daca pica nodul apasat ("If this fails"): chenar si linie rosii. */
      selector: '.imap-hurt',
      style: { 'border-width': 3, 'border-style': 'solid', 'border-color': '#d9534f', 'line-color': '#d9534f', opacity: 1 },
    },
    {
      selector: '.imap-culprit',
      style: { 'border-width': 5, 'border-color': '#d9534f', 'border-style': 'double', opacity: 1 },
    },
    {
      selector: '.imap-dim',
      style: { opacity: 0.12 },
    },
    {
      /* Ce se potriveste cautarii: chenar galben, restul stins. */
      selector: '.imap-hit',
      style: { 'border-width': 3, 'border-color': '#f0ad4e', 'border-style': 'solid' },
    },
    {
      /*
       * Cutia unui sediu (nod parinte, pe "All offices"): dreptunghi cu margine
       * punctata si numele sediului sus. Fara iconita - regula `node` de sus i-ar
       * da-o pe cea de "discovered".
       *
       * Sta DUPA `.imap-dim` si nu se stinge niciodata: in cytoscape opacitatea
       * parintelui se inmulteste in copii, deci o cutie stinsa ar stinge si
       * aparatul gasit de cautare sau lovit de "If this fails".
       */
      selector: 'node[?box]',
      style: {
        'background-image': 'none',
        'background-color': '#eef2f6',
        'background-opacity': 0.35,
        'border-width': 1.5,
        'border-style': 'dashed',
        'border-color': '#9aa4b1',
        shape: 'round-rectangle',
        padding: BOX_PADDING,
        label: 'data(label)',
        /*
         * Numele sus-stanga: `left` pe un parinte pune eticheta IN AFARA cutiei,
         * cu capatul drept lipit de marginea din stanga. O impingem inapoi
         * inauntru cu propria ei latime - care atarna doar de text, nu de
         * marimea cutiei, deci ramane la locul ei si cand se muta aparatele.
         */
        'text-valign': 'top',
        'text-halign': 'left',
        'text-margin-x': (el: StyleEl) => labelWidth(String(el.data('label') ?? ''), BOX_FONT) + 10,
        'text-margin-y': -6,
        'text-max-width': 600,
        'text-wrap': 'none',
        'font-size': BOX_FONT,
        'font-weight': 'bold',
        color: '#5a6570',
        opacity: 1,
      },
    },
  ]
}

/* Cat loc primeste un nod pe inelul lui, cu numele cu tot. */
const ARC = 66

/* Cat de departe stau doua inele unul de altul, cel putin. */
const RING = 108

/* Cat de mult are voie sa se MAREASCA harta la incadrare. */
const MAX_ZOOM_FIT = 1.4

type RawNode = { id?: unknown; data?: unknown } & Record<string, unknown>
type RawEdge = { id?: unknown; source?: unknown; target?: unknown; data?: unknown } & Record<string, unknown>

function dataOf(item: RawNode | RawEdge): Record<string, unknown> {
  if (item && typeof item.data === 'object' && item.data) {
    return item.data as Record<string, unknown>
  }
  return item as Record<string, unknown>
}

/**
 * Nodurile si muchiile, gata de dat lui cytoscape.
 *
 * O MUCHIE CARE ATINGE UN NOD ABSENT SE ARUNCA. Cand se stinge "Include
 * Auto-Discovered Items", nodurile `unregistered` dispar, dar muchiile lor
 * raman in raspuns - iar cytoscape ARUNCA la o muchie fara capat, si atunci nu
 * se mai deseneaza NIMIC. Portalul vechi le sarea la fel (`hidden[e.source]`);
 * aici filtrul e in acelasi loc cu desenul, ca sa prinda orice nod scos, nu
 * doar pe cele nefiltrate de apelant.
 */
export function mapElements(graph: { nodes?: unknown[]; edges?: unknown[] }): { data: Record<string, unknown> }[] {
  const ids = new Set<string>()
  const nodeEls = ((graph.nodes as RawNode[]) ?? []).map((n) => {
    const data = dataOf(n)
    const id = String(n.id ?? data.id ?? '')
    ids.add(id)
    return { data: { ...data, id } }
  })

  const edgeEls: { data: Record<string, unknown> }[] = []
  ;((graph.edges as RawEdge[]) ?? []).forEach((e, i) => {
    const data = dataOf(e)
    const source = String(e.source ?? data.source ?? '')
    const target = String(e.target ?? data.target ?? '')
    if (!ids.has(source) || !ids.has(target)) {
      return
    }
    edgeEls.push({ data: { ...data, id: String(e.id ?? data.id ?? 'e' + i), source, target } })
  })

  return [...nodeEls, ...edgeEls]
}

/**
 * Asezarea in inele: internetul in mijloc, si fiecare lucru in dreptul alor lui.
 *
 * E `radial()` din inventory_map.js, calculat aici in TypeScript pur (fara
 * cytoscape) ca sa poata fi si probat. Fiecare nod primeste o FELIE de cerc, iar
 * copiii lui se aseaza inauntrul feliei parintelui - deci un comutator si
 * masinile lui stau mereu in acelasi dreptul, si nicio linie nu taie prin mijloc.
 * Felia se imparte dupa cate frunze are fiecare ramura. Razele nu sunt fixe:
 * fiecare inel e impins destul cat sa incapa pe circumferinta lui toate nodurile.
 *
 * `rankOf` (pe "All offices", cu cutii): copiii fiecarui nod se ordoneaza dupa
 * sediu, ca aparatele unui sediu sa ocupe un arc continuu si cutia lui sa nu
 * se intinda peste tot cercul. Un nod din afara cutiilor (o iesire publica, o
 * poarta comuna) merge dupa cel mai mic sediu de sub el. Sortare stabila: la
 * egalitate, ordinea de pana acum. Fara `rankOf`, cercul e neschimbat.
 */
export function radialPositions(
  nodes: unknown[],
  edges: unknown[],
  rankOf?: (id: string) => number | undefined,
): Record<string, { x: number; y: number }> {
  const ids = new Set<string>()
  for (const n of (nodes as RawNode[]) ?? []) {
    ids.add(String(n.id ?? dataOf(n).id ?? ''))
  }

  const adjacency = new Map<string, string[]>()
  for (const e of (edges as RawEdge[]) ?? []) {
    const data = dataOf(e)
    const source = String(e.source ?? data.source ?? '')
    const target = String(e.target ?? data.target ?? '')
    if (!ids.has(source) || !ids.has(target)) {
      continue
    }
    const list = adjacency.get(source) ?? []
    list.push(target)
    adjacency.set(source, list)
  }

  const depth = new Map<string, number>()
  const children = new Map<string, string[]>()
  const perDepth = new Map<number, number>()
  const visited = new Set<string>()
  let deepest = 0
  const root = 'internet'

  if (ids.has(root)) {
    const queue = [root]
    visited.add(root)
    depth.set(root, 0)
    perDepth.set(0, 1)
    while (queue.length) {
      const id = queue.shift() as string
      const here = depth.get(id) ?? 0
      for (const next of adjacency.get(id) ?? []) {
        if (visited.has(next)) {
          continue
        }
        visited.add(next)
        depth.set(next, here + 1)
        const kids = children.get(id) ?? []
        kids.push(next)
        children.set(id, kids)
        perDepth.set(here + 1, (perDepth.get(here + 1) ?? 0) + 1)
        if (here + 1 > deepest) {
          deepest = here + 1
        }
        queue.push(next)
      }
    }
  }

  /* Ce n-a fost atins se agata de centru, ca sa nu ramana fara loc. */
  const orphans: string[] = []
  for (const id of ids) {
    if (!visited.has(id) && id !== root) {
      orphans.push(id)
    }
  }
  if (orphans.length) {
    deepest = Math.max(deepest, 1)
    perDepth.set(1, (perDepth.get(1) ?? 0) + orphans.length)
    const kids = children.get(root) ?? []
    for (const id of orphans) {
      depth.set(id, 1)
      kids.push(id)
      visited.add(id)
    }
    children.set(root, kids)
  }

  if (rankOf) {
    const keyOf = new Map<string, number>()
    const keyFor = (id: string): number => {
      const known = keyOf.get(id)
      if (known !== undefined) return known
      let key = rankOf(id) ?? Infinity
      if (key === Infinity) {
        for (const child of children.get(id) ?? []) key = Math.min(key, keyFor(child))
      }
      keyOf.set(id, key)
      return key
    }
    for (const [id, kids] of children) {
      const sorted = kids.map((kid, i) => ({ kid, i, key: keyFor(kid) })).sort((a, b) => a.key - b.key || a.i - b.i)
      children.set(id, sorted.map((s) => s.kid))
    }
  }

  /* Cate frunze atarna sub fiecare nod: dupa asta se imparte felia. */
  const leaves = new Map<string, number>()
  function countLeaves(id: string): number {
    const mine = children.get(id)
    if (!mine || !mine.length) {
      leaves.set(id, 1)
      return 1
    }
    let total = 0
    for (const child of mine) {
      total += countLeaves(child)
    }
    leaves.set(id, total)
    return total
  }
  countLeaves(root)

  /* Raza fiecarui inel: destula cat sa incapa toate nodurile de pe el. */
  const radius = [0]
  for (let d = 1; d <= deepest; d++) {
    const needed = ((perDepth.get(d) ?? 1) * ARC) / (2 * Math.PI)
    radius[d] = Math.max(radius[d - 1] + RING, needed)
  }

  const places: Record<string, { x: number; y: number }> = { internet: { x: 0, y: 0 } }
  function place(id: string, from: number, to: number): void {
    const mine = children.get(id) ?? []
    const span = to - from
    const parentLeaves = leaves.get(id) || 1
    let cursor = from
    for (const child of mine) {
      const slice = span * ((leaves.get(child) || 1) / parentLeaves)
      const middle = cursor + slice / 2
      const r = radius[depth.get(child) ?? 1] || RING
      places[child] = { x: r * Math.cos(middle), y: r * Math.sin(middle) }
      place(child, cursor, cursor + slice)
      cursor += slice
    }
  }
  /* Se porneste de sus, nu de la dreapta: prima ramura iese deasupra centrului. */
  place(root, -Math.PI / 2, (3 * Math.PI) / 2)

  return places
}

/**
 * Ce se stinge daca pica nodul apasat ("If this fails").
 *
 * `successors()` da tot ce atarna dedesubt, oricat de adanc - harta e un lant de
 * la internet in jos, deci tot ce e sub un nod trece prin el. Intoarce cate
 * DISPOZITIVE raman fara drum, ca sa se scrie langa buton.
 */
export function showImpact(cy: Cy, id: string): number {
  const node = cy.$id(id)
  if (!node.length) {
    return 0
  }
  const hurt = node.successors()
  cy.elements().removeClass('imap-dim imap-hit imap-hurt imap-culprit').addClass('imap-dim')
  hurt.removeClass('imap-dim').addClass('imap-hurt')
  node.removeClass('imap-dim').addClass('imap-culprit')
  node.predecessors().removeClass('imap-dim')
  return hurt.nodes().filter((n) => !!n.data('deviceId')).length
}

/** Stinge orice evidentiere (impact sau cautare). */
export function clearHighlight(cy: Cy): void {
  cy.elements().removeClass('imap-dim imap-hit imap-hurt imap-culprit')
}

/**
 * Cautarea: ce se potriveste se aprinde, restul se stinge - ca in inventory_map.js.
 * Nu se scot nodurile care nu se potrivesc, ca sa nu-si schimbe harta forma la
 * fiecare litera; stinse, raman la locul lor si se vede UNDE e ce ai cautat.
 */
export function applySearch(cy: Cy, term: string): void {
  cy.elements().removeClass('imap-dim imap-hit imap-hurt imap-culprit')
  const needle = term.trim().toLowerCase()
  if (needle === '') {
    return
  }
  const hits = cy.nodes().filter((n) => {
    /* Cutia "Office 1" nu e un rezultat cand cauti "office". */
    if (n.data('box')) return false
    const text = (String(n.data('label') ?? '') + ' ' + String(n.data('sublabel') ?? '')).toLowerCase()
    return text.indexOf(needle) !== -1
  })
  if (!hits.length) {
    return
  }
  cy.elements().addClass('imap-dim')
  hits.removeClass('imap-dim').addClass('imap-hit')
  hits.predecessors().removeClass('imap-dim')
}


/** Cutia unui sediu (nod parinte): n-are pozitie proprie si nu e un aparat. */
function isBox(node: { data: (k?: string) => unknown }): boolean {
  return typeof node.data === 'function' && !!node.data('box')
}

/** Cheia din localStorage: sediul ales, ca pe portal (`imap.pos.<office>`). */
export function positionsKey(scope: string): string {
  return 'imap.pos.' + (scope || 'all')
}

export function readPositions(scope: string): Record<string, { x: number; y: number }> {
  try {
    return (JSON.parse(window.localStorage.getItem(positionsKey(scope)) || 'null') as Record<string, { x: number; y: number }> | null) || {}
  } catch {
    return {}
  }
}

/** Dupa ce omul muta un nod, se tin minte toate pozitiile din desen. */
export function writePositions(cy: Cy, scope: string): void {
  const kept: Record<string, { x: number; y: number }> = {}
  cy.nodes().forEach((node) => {
    /* Cutia de sediu n-are pozitie a ei: o socoteste cytoscape din copii. */
    if (isBox(node)) return
    const at = node.position()
    kept[node.id()] = { x: Math.round(at.x), y: Math.round(at.y) }
  })
  try {
    window.localStorage.setItem(positionsKey(scope), JSON.stringify(kept))
  } catch {
    /* privat / plin - harta merge mai departe fara memorie */
  }
}

export function forgetPositions(scope: string): void {
  try {
    window.localStorage.removeItem(positionsKey(scope))
  } catch {
    /* */
  }
}

/** Pune inapoi ce s-a mutat cu mana, peste asezarea automata. */
export function applyPositions(cy: Cy, scope: string): boolean {
  const kept = readPositions(scope)
  let any = false
  cy.nodes().forEach((node) => {
    /* Pusa pe o cutie, pozitia ar muta toti copiii ei. */
    if (isBox(node)) return
    const at = kept[node.id()]
    if (at) {
      node.position(at)
      any = true
    }
  })
  return any
}

export interface MapHandlers {
  /** Clic pe un nod: se deschide panoul din dreapta cu ce se stie despre el. */
  onTap?: (node: Record<string, unknown>) => void
  /** Clic-dreapta pe un nod: editarea rapida de langa el. */
  onContext?: (node: Record<string, unknown>) => void
  /** Clic pe panza goala: se inchide panoul. */
  onBackground?: () => void
  /**
   * Unde se tin minte pozitiile mutate cu mana (`imap.pos.<scope>`).
   * Gol = toate sediile. Fara el, mutarile se pierd la reimprospatare.
   */
  positionsScope?: string
  /**
   * Cutiile de sediu (`groupByOffice`), pe "All offices". Fara ele - un singur
   * sediu - harta se aseaza ca pana acum, in inele din internet.
   */
  officeGroups?: OfficeGroups | null
}

/**
 * Deseneaza harta si leaga clicurile: stanga deschide panoul din dreapta,
 * dreapta deschide editarea rapida, iar un clic pe gol le inchide.
 */

/**
 * Panza isi schimba latimea (panoul din dreapta se deschide/inchide), dar
 * zoom-ul ales de om ramane. `resize` spune motorului noua cutie; `fit` ar
 * arunca zoom-ul manual si ar reincadra totul - de-aia nu il chemam aici.
 */
export function keepViewport(cy: Pick<Cy, 'resize' | 'zoom' | 'pan'>): void {
  const zoom = cy.zoom()
  const pan = cy.pan()
  cy.resize()
  cy.zoom(zoom)
  cy.pan({ x: pan.x, y: pan.y })
}

/**
 * Incadreaza toata harta (butonul Fit, tasta 0, si prima desenare), cu
 * acelasi prag de sus: pe o harta de trei noduri, umplerea panzei ar umfla
 * iconitele cat niste butoane.
 */
export function fitMap(cy: Pick<Cy, 'fit' | 'zoom' | 'center'>): void {
  cy.fit(undefined, 30)
  if (cy.zoom() > MAX_ZOOM_FIT) {
    cy.zoom(MAX_ZOOM_FIT)
    cy.center()
  }
}

export async function drawMap(container: HTMLElement, graph: { nodes?: unknown[]; edges?: unknown[] }, handlers: MapHandlers = {}): Promise<Cy | null> {
  const cytoscape = await loadCytoscape()
  const groups = handlers.officeGroups ?? null
  const elements = mapElements(graph)
  if (groups) {
    for (const el of elements) {
      const parent = groups.parentOf[String(el.data.id)]
      if (parent && el.data.source === undefined) el.data.parent = parent
    }
    elements.unshift(...boxElements(groups))
  }
  const scope = handlers.positionsScope || 'all'
  const radial = radialPositions(graph.nodes ?? [], graph.edges ?? [], groups ? officeRank(groups) : undefined)
  /*
   * Ca pe portal: pozitiile mutate cu mana Intra in asezarea initiala, nu se
   * lipesc dupa. Altfel, la fiecare re-desenare (refetch, focus pe fereastra)
   * nodurile sareau o clipa la radial si, daca salvarea intarzia, faceau
   * impresia ca mutarea s-a pierdut.
   */
  const kept = readPositions(scope)
  const positions: Record<string, { x: number; y: number }> = { ...radial }
  for (const [id, at] of Object.entries(kept)) {
    if (radial[id]) {
      positions[id] = at
    }
  }

  const cy = cytoscape({
    container,
    elements,
    style: mapStyle(),
    layout: { name: 'preset', positions, fit: false },
    /*
     * Fara `wheelSensitivity`: rotita o prinde `attachWheelZoom` (map/zoom.ts)
     * inainte sa ajunga la Cytoscape - cu 0.2, o treapta facea sub 1%.
     */
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
  })

  /*
   * Incadrata dupa ce asezarea s-a asezat, si strunita de un prag de sus: pe o
   * harta de trei noduri, umplerea panzei ar umfla iconitele cat niste butoane.
   */
  cy.ready(() => {
    applyPositions(cy, scope)
    cy.resize()
    fitMap(cy)
  })

  /* `dragfree` e evenimentul din cytoscape 3; `free` acopera build-urile mai vechi. */
  const remember = () => writePositions(cy, scope)
  cy.on('dragfree', 'node', remember)
  cy.on('free', 'node', remember)

  if (handlers.onTap) {
    const onTap = handlers.onTap
    const onBox = handlers.onBackground
    cy.on('tap', 'node', (ev) => {
      const data = ev.target.data() as Record<string, unknown>
      /* Clic in cutia unui sediu (pe gol, intre aparate) = clic pe panza goala. */
      if (data.box) {
        onBox?.()
        return
      }
      onTap(data)
    })
  }
  if (handlers.onContext) {
    const onContext = handlers.onContext
    cy.on('cxttap', 'node', (ev) => {
      const data = ev.target.data() as Record<string, unknown>
      if (!data.box) onContext(data)
    })
  }
  if (handlers.onBackground) {
    const onBackground = handlers.onBackground
    cy.on('tap', (ev) => {
      if (ev.target === cy) {
        onBackground()
      }
    })
  }

  return cy
}
